import { useState } from "react";
import api from "@/lib/api";
import { PageHeader, Panel } from "@/components/common";
import { TextT, Sparkle, Spinner, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

const SCRIPT = [];

const TOOLS = ["Rewrite", "Funnier", "Shorter", "More Emotional", "More Educational", "More Exciting", "Simplify for Children"];

export default function ScriptEditor() {
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const runTool = async (tool) => {
    if (!selected) { toast.error("Select a line first"); return; }
    setBusy(true); setSuggestion(null);
    try {
      const { data } = await api.post("/ai/ask", { text: `Rewrite this line of a children's cartoon script to be "${tool}". Return only the improved line, no quotes: ${selected.t}` });
      setSuggestion(data.reply);
    } catch { toast.error("AI rewrite failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader icon={TextT} title="Script Editor"
        subtitle="A screenplay editor with direct text editing and AI editing. Select a line, then apply an AI tool or voice command." />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          {SCRIPT.length === 0 && <Panel><p className="text-sm text-zinc-400">No script loaded. Create or open a PinPilot campaign to edit its real scenes.</p></Panel>}
          {SCRIPT.map((sc) => (
            <Panel key={sc.scene}>
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-xs text-[#00E5FF]">SCENE {sc.scene}</span>
                <span className="text-xs text-zinc-500">{sc.location}</span>
              </div>
              <div className="space-y-3">
                {sc.lines.map((ln, i) => {
                  const isSel = selected?.t === ln.t;
                  return (
                    <button key={i} data-testid={`script-line-${sc.scene}-${i}`} onClick={() => { setSelected(ln); setSuggestion(null); }}
                      className={`w-full text-left rounded-lg px-4 py-3 border transition-colors ${isSel ? "border-[#00E5FF]/50 bg-[#00E5FF]/8" : "border-transparent hover:bg-white/5"}`}>
                      <div className="font-mono text-xs text-zinc-500 mb-0.5">{ln.c}</div>
                      <div className="text-white">{ln.t}</div>
                    </button>
                  );
                })}
              </div>
            </Panel>
          ))}
        </div>

        <div className="space-y-5">
          <Panel>
            <div className="flex items-center gap-2 mb-3 text-sm text-zinc-400"><Sparkle size={16} weight="fill" className="text-[#00E5FF]" /> AI Editing Tools</div>
            <div className="flex flex-wrap gap-2">
              {TOOLS.map((t) => (
                <button key={t} data-testid={`tool-${t.replace(/ /g, "-")}`} onClick={() => runTool(t)} disabled={busy}
                  className="rounded-full border border-white/10 bg-[#1C1C1C] px-3 py-1.5 text-xs text-white hover:border-[#00E5FF]/40 transition-colors disabled:opacity-50">{t}</button>
              ))}
            </div>
            {selected && <div className="mt-4 text-xs text-zinc-500">Selected: <span className="text-zinc-300">"{selected.t}"</span></div>}
            {busy && <div className="mt-4 flex items-center gap-2 text-sm text-zinc-400"><Spinner size={16} className="animate-spin" /> Rewriting…</div>}
            {suggestion && (
              <div className="mt-4 rounded-xl border border-[#00FF9D]/30 bg-[#00FF9D]/8 p-3">
                <div className="text-xs text-[#00FF9D] mb-1">AI suggestion</div>
                <p className="text-sm text-white">{suggestion}</p>
                <button onClick={() => toast.success("Applied to script")} className="mt-2 text-xs text-[#00E5FF] hover:underline">Apply</button>
              </div>
            )}
          </Panel>
          <Panel>
            <div className="flex items-center gap-2 mb-2 text-sm text-white"><Warning size={16} weight="fill" className="text-[#FFB800]" /> Affected scenes</div>
            <p className="text-xs text-zinc-400">When a real scene is selected, Studio AI can identify and update dependent scenes.</p>
            <button onClick={() => toast("Select a real script scene first.")} className="mt-3 w-full rounded-full bg-white/5 border border-white/10 py-2 text-sm text-white hover:border-white/25 transition-colors">Update Affected Scenes</button>
          </Panel>
        </div>
      </div>
    </div>
  );
}
